/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import Dropzone, { Accept, DropEvent, FileRejection } from 'react-dropzone';

import { Icon, Text } from '../index';
import { convertBytesToMbsOrKbs, readFile } from './helpers';
import PreviewList from './preview-list';
import { IAlertType, IResource, ResourceReaderFunction } from '@wyz/types';
import { useDropzoneContext } from './context';
import { useStateWithCallback } from '@wyz/utils';
const DropIcon = () => (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={80}
    height={60}
    viewBox='0 0 148 104'
    fill='none'
  >
    <g clipPath='url(#clip0_2549_2807)'>
      <path
        d='M16.7339 104.001H131.417L148 10.1955H73.4837L72.5672 0.481934H35.0339L16.7339 104.001Z'
        fill='#CAD6E2'
      />
      <path
        d='M130.863 104H16.2701L0 22.5075H114.593L130.863 104Z'
        fill='#E6EDF5'
      />
    </g>
    <defs>
      <clipPath id='clip0_2549_2807'>
        <rect width={148} height={104} fill='white' />
      </clipPath>
    </defs>
  </svg>
);

const classes = {
  root: {
    position: 'relative',
    width: '100%',
    minHeight: '150px',
    border: '1px dashed #ced4da',
    borderRadius: '0.25rem',
    cursor: 'pointer',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
  },
  active: {
    animation: '$progress 2s linear infinite !important',
    backgroundImage: `repeating-linear-gradient(-45deg, ${'#ccc'}, ${'#ccc'} 25px, ${'#000'} 25px, ${'#000'} 50px)`,
    backgroundSize: '150% 100%',
    border: 'solid',
    borderColor: '#ffff',
  },
  invalid: {
    backgroundImage: `repeating-linear-gradient(-45deg, ${'#fff'}, ${'#fff'} 25px, ${'#000'} 25px, ${'#000'} 50px)`,
    borderColor: '#000',
  },
  textContainer: {
    textAlign: 'center',
  },
  text: {
    marginBottom: '1rem',
    marginTop: '1rem',
  },
  icon: {
    width: 51,
    height: 51,
    color: '#000',
  },
};

const DEFAULTS = {
  getFileLimitExceedMessage: (filesLimit: number) =>
    `Maximum allowed number of files exceeded. Only ${filesLimit} allowed`,
  getFileAddedMessage: (fileName: string): string =>
    `File ${fileName} successfully added.`,
  getDropRejectMessage: (
    rejectedFile: FileRejection,
    acceptedFiles: Accept,
    maxFileSize: number,
  ) => {
    let message = `File ${rejectedFile.file.name} was rejected. `;
    if (!acceptedFiles[rejectedFile.file.type]) {
      message += 'File type not supported. ';
    }
    if (rejectedFile.file.size > maxFileSize) {
      message +=
        'File is too big. Size limit is ' +
        convertBytesToMbsOrKbs(maxFileSize) +
        '. ';
    }
    return message;
  },
  getFileRemovedMessage: (fileName: string) => `File ${fileName} removed.`,
};

type AcceptVariant = 'IMAGE' | 'PDF' | 'CSV' | 'XLSX';
const AcceptFilesLookup: Record<
  AcceptVariant,
  { [key: string]: Array<string> }
> = {
  IMAGE: {
    'image/png': ['.png'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/svg': ['.svg'],
    'image/webp': ['.webp'],
  },
  PDF: { 'application/pdf': ['.pdf'] },
  CSV: { 'text/csv': ['.csv'] },
  XLSX: {
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
      '.xlsx',
    ],
  },
};
export interface DropzoneAreaBaseProps {
  /** @ignore */
  classes?: Record<string, string>;
  /** A list of file types to accept.
   * @see See [here](https://react-dropzone.js.org/#section-accepting-specific-file-types) for more details.
   */
  acceptedFiles?: AcceptVariant;
  /** Maximum number of files that can be loaded into the dropzone. */
  filesLimit?: number;
  /** Icon to be displayed inside the dropzone area. */
  Icon?: React.ElementType;
  /** Currently loaded files. */
  fileObjects?: FileObjectShape[];
  /** Maximum file size (in bytes) that the dropzone will accept. */
  maxFileSize?: number;
  /** Text inside the dropzone. */
  dropzoneText?: React.ReactNode;
  /** Disable feedback effect when dropping rejected files. */
  disableRejectionFeedback?: boolean;
  /** Shows previews **BELOW** the dropzone. */
  showPreviews?: boolean;
  /** The label for the file preview section. */
  previewText?: string;
  /**
   * Props to pass to the Dropzone component.
   *
   * @see See [Dropzone props](https://react-dropzone.js.org/#src) for available values.
   */
  dropzoneProps?: Record<string, any>;
  /**
   * Attributes applied to the input element.
   *
   * @see See [MDN Input File attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#Additional_attributes) for available values.
   */
  inputProps?: Record<string, any>;
  /**
   * Get alert message to display when files limit is exceed.
   *
   * *Default*: "Maximum allowed number of files exceeded. Only ${filesLimit} allowed"
   *
   * @param {number} filesLimit The `filesLimit` currently set for the component.
   */
  getFileLimitExceedMessage?(...args: unknown[]): string;
  /**
   * Get alert message to display when a new file is added.
   *
   * *Default*: "File ${fileName} successfully added."
   *
   * @param {string} fileName The newly added file name.
   */
  getFileAddedMessage?(...args: unknown[]): string;
  /**
   * Get alert message to display when a file is removed.
   *
   * *Default*: "File ${fileName} removed."
   *
   * @param {string} fileName The name of the removed file.
   */
  getFileRemovedMessage?(...args: unknown[]): string;
  /**
   * Get alert message to display when a file is rejected onDrop.
   *
   * *Default*: "File ${rejectedFile.name} was rejected."
   *
   * @param {Object} rejectedFile The file that got rejected
   * @param {string[]} acceptedFiles The `acceptedFiles` prop currently set for the component
   * @param {number} maxFileSize The `maxFileSize` prop currently set for the component
   */
  getDropRejectMessage?(...args: unknown[]): unknown;
  /**
   * A function which determines which icon to display for a file preview.
   *
   * *Default*: If its an image then displays a preview the image, otherwise it will display an attachment icon
   *
   * @param {FileObject} objectFile The file which the preview will belong to
   * @param {Object} classes The classes for the file preview icon, in the default case we use the 'image' className.
   */
  getPreviewIcon?(...args: unknown[]): unknown;
  /**
   * Fired when new files are added to dropzone.
   *
   * @param {FileObject[]} newFiles The new files added to the dropzone.
   */
  onAdd?(...args: unknown[]): unknown;
  /**
   * Fired when a file is deleted from the previews panel.
   *
   * @param {FileObject} deletedFileObject The file that was removed.
   * @param {number} index The index of the removed file object.
   */
  onDelete?(fileObject: FileObjectShape, index: number): unknown;
  /**
   * Fired when the user drops files into the dropzone.
   *
   * @param {File[]} droppedFiles All the files dropped into the dropzone.
   * @param {Event} event The react-dropzone drop event.
   */
  onDrop?(...args: unknown[]): unknown;
  /**
   * Fired when a file is rejected because of wrong file type, size or goes beyond the filesLimit.
   *
   * @param {File[]} rejectedFiles All the rejected files.
   * @param {Event} event The react-dropzone drop event.
   */
  onDropRejected?(...args: unknown[]): unknown;
  /**
   * Fired when an alert is triggered.
   *
   * @param {string} message Alert message.
   * @param {string} variant One of "error", "info", "success".
   */
  onAlert?(message: string, variant: IAlertType): unknown;
  resourceReader?: ResourceReaderFunction;
}

type State = {
  openSnackBar: boolean;
  snackbarMessage: string;
  snackbarVariant: IAlertType;
};

/**
 * This components creates Dropzone, with previews and snackbar notifications.
 */
const DropzoneAreaBase = (props: DropzoneAreaBaseProps) => {
  const [, setState] = useStateWithCallback<State>({
    openSnackBar: false,
    snackbarMessage: '',
    snackbarVariant: 'success',
  });
  const {
    acceptedFiles = 'IMAGE',
    disableRejectionFeedback = false,
    dropzoneProps,
    fileObjects = [],
    filesLimit = 3,
    inputProps,
    maxFileSize = 3_000_000,
    showPreviews = false, // By default previews show up under in the dialog and inside in the standalone
    resourceReader,
  } = props;
  function notifyAlert(nextState: State) {
    const { onAlert } = props;
    if (nextState.openSnackBar && onAlert) {
      onAlert(nextState.snackbarMessage, nextState.snackbarVariant);
    }
  }

  const handleDropAccepted = async (
    acceptedFiles: Array<File>,
    event_: DropEvent,
  ) => {
    const {
      fileObjects = [],
      filesLimit = 3,
      // getFileAddedMessage = DEFAULTS.getFileAddedMessage,
      getFileLimitExceedMessage = DEFAULTS.getFileLimitExceedMessage,
      onAdd,
      onDrop,
    } = props;
    if (
      filesLimit > 1 &&
      fileObjects.length + acceptedFiles.length > filesLimit
    ) {
      setState(
        {
          openSnackBar: true,
          snackbarMessage: getFileLimitExceedMessage(filesLimit),
          snackbarVariant: 'error',
        },
        notifyAlert,
      );
      return;
    }

    // Notify Drop event
    if (onDrop) {
      onDrop(acceptedFiles, event_);
    }

    // Retrieve fileObjects data
    const fileObjs = await Promise.all(
      acceptedFiles.map(async (file) => {
        const data = await readFile(file);
        return {
          file,
          data,
        };
      }),
    );

    // Notify added files
    if (onAdd) {
      onAdd(fileObjs);
    }

    // Display message

    // const message = fileObjs.reduce(
    //   (message_, fileObject: FileObjectShape) =>
    //     `${message_} ${getFileAddedMessage(fileObject.file.name) as string}`,
    //   ''
    // );
    // setState(
    //   {
    //     openSnackBar: true,
    //     snackbarMessage: message,
    //     snackbarVariant: INotifierEnum.Success,
    //   },
    //   notifyAlert
    // );
  };

  const handleDropRejected = (rejectedFiles: Array<any>, event_: DropEvent) => {
    const {
      acceptedFiles,
      filesLimit = 3,
      fileObjects = [],
      getDropRejectMessage = DEFAULTS.getDropRejectMessage,
      getFileLimitExceedMessage = DEFAULTS.getFileLimitExceedMessage,
      maxFileSize,
      onDropRejected,
    } = props;

    let message = '';

    if (
      // only relevant for multi-file mode: files accumulate across drops, so we need to check the combined total.
      // in single-file mode (filesLimit === 1), the new file simply replaces the existing one, so no limit check is needed.
      filesLimit > 1 &&
      fileObjects.length + rejectedFiles.length > filesLimit
    ) {
      message = getFileLimitExceedMessage(filesLimit);
    } else {
      rejectedFiles.forEach((rejectedFile) => {
        message = getDropRejectMessage(
          rejectedFile,
          acceptedFiles,
          maxFileSize,
        ) as string;
      });
    }

    if (onDropRejected) {
      onDropRejected(rejectedFiles, event_);
    }
    setState(
      {
        openSnackBar: true,
        snackbarMessage: message,
        snackbarVariant: 'error',
      },
      notifyAlert,
    );
  };

  const handleRemove = (fileIndex: number) => (event: React.MouseEvent) => {
    event.stopPropagation();

    const {
      fileObjects = [],
      getFileRemovedMessage = DEFAULTS.getFileRemovedMessage,
      onDelete,
    } = props;

    // Find removed fileObject
    const removedFileObject = fileObjects[fileIndex];
    if (!removedFileObject) {
      return;
    }
    if (onDelete) {
      // Notify removed file
      onDelete(removedFileObject, fileIndex);
    }
    setState(
      {
        openSnackBar: true,
        snackbarMessage: getFileRemovedMessage(
          removedFileObject.file?.name ??
            removedFileObject.resource?.originalFileName,
        ),
        snackbarVariant: 'info',
      },
      notifyAlert,
    );
  };

  const context = useDropzoneContext();
  const isMultiple = filesLimit > 1;
  const previewsVisible = showPreviews && fileObjects.length > 0;

  return (
    <React.Fragment>
      <Dropzone
        {...dropzoneProps}
        accept={AcceptFilesLookup[acceptedFiles]}
        onDropAccepted={handleDropAccepted}
        onDropRejected={handleDropRejected}
        maxSize={maxFileSize}
        multiple={isMultiple}
      >
        {({ getRootProps, getInputProps, isDragActive, isDragReject }) => (
          <div
            data-cy={inputProps?.name}
            {...getRootProps({
              style: {
                ...classes.root,
                ...(isDragActive && classes.active),
                ...(!disableRejectionFeedback &&
                  isDragReject &&
                  classes.invalid),
              } as React.CSSProperties,
            })}
            className='bg-neutral-grey-1'
          >
            <input {...getInputProps(inputProps)} />

            <div className='space-y-4 '>
              <DropIcon />
              <Text heading='normal' color='secondary-gray'>
                <Icon icon='plus' />
                {context.translations.dropFilesMessage}
              </Text>

              {context.translations.dropFilesDescription && (
                <Text heading='xs' color='secondary'>
                  {context.translations.dropFilesDescription}
                </Text>
              )}
            </div>
          </div>
        )}
      </Dropzone>
      {previewsVisible && (
        <PreviewList
          fileObjects={fileObjects}
          onRemove={handleRemove}
          resourceReader={resourceReader}
        />
      )}
    </React.Fragment>
  );
};

export type FileObjectShape = {
  file?: File;
  data?: any;
  resource?: IResource;
};

export default DropzoneAreaBase;
